import { Alert, Button, Card, Spin, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { pdf } from "@react-pdf/renderer";
import {
  LeaveApplicationPDFRenderer,
  LeaveApplicationPDFTemplateType,
} from "../context/nonVisual/LeaveApplicationPDF";
import { formatDate } from "../utils/helpers";
import { apiClient } from "../utils/httpClient";
import { resolvePdfDaysSplit } from "../utils/pdfDaysSplit";

type EmailActionResult =
  | "SUCCESS"
  | "NEEDS_SIGNATURE"
  | "EXPIRED"
  | "ALREADY_USED"
  | "INVALID"
  | "NO_LONGER_ACTIONABLE";

type EmailActionResponse = {
  result: EmailActionResult;
  message: string;
  requestContext?: EmailActionRequestContext;
};

type EmailActionRequestContext = {
  employeeName: string;
  employeeJobTitle: string;
  employeeGender?: string | null;
  requestType?: string | null;
  substituteName?: string | null;
  startDate: string;
  endDate: string;
  duration: number;
  requestYear: number;
  employeeSignature: string;
  previousYearDaysUsedForRequest?: number;
  currentYearDaysUsedForRequest?: number;
  usedPreviousYearDays?: number;
  usedCurrentYearDays?: number;
  used_previous_year_days?: number;
  used_current_year_days?: number;
  previous_year_days_used_for_request?: number;
  current_year_days_used_for_request?: number;
  managerSignatureImage?: string;
  unpaidLeaveReason?: string | null;
};

const extractTokenFromHash = (): string | null => {
  const hash = window.location.hash?.startsWith("#")
    ? window.location.hash.substring(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  return params.get("token");
};

const EmailAction = () => {
  const [isExecuting, setIsExecuting] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [response, setResponse] = useState<EmailActionResponse | null>(null);
  const [generatedPdf, setGeneratedPdf] = useState<string | null>(null);
  const requestStarted = useRef(false);
  const autoSignAttempted = useRef(false);
  const signatureRef = useRef<SignatureCanvas | null>(null);

  const executeAction = async (leavePDF?: string) => {
    const token = extractTokenFromHash();

    if (!token) {
      setResponse({
        result: "INVALID",
        message: "The action link is invalid or missing a token.",
      });
      setIsExecuting(false);
      return;
    }

    setIsExecuting(true);
    try {
      const payload = leavePDF ? { token, leavePDF } : { token };
      const { data } = await apiClient.post<EmailActionResponse>(
        `${process.env.REACT_APP_API_URL}/public/email-actions/execute`,
        payload,
      );
      setResponse(data);
    } catch {
      setResponse({
        result: "INVALID",
        message: "The action could not be completed.",
      });
    } finally {
      setIsExecuting(false);
    }
  };

  useEffect(() => {
    if (requestStarted.current) {
      return;
    }
    requestStarted.current = true;
    executeAction();
  }, []);

  const handleClearSignature = () => {
    if (!signatureRef.current) {
      return;
    }
    signatureRef.current.clear();
    setGeneratedPdf(null);
  };

  const handleApproveWithSignature = () => {
    if (!generatedPdf) {
      setResponse({
        result: "NEEDS_SIGNATURE",
        message: "Please save your signature first so we can generate the PDF.",
        requestContext: response?.requestContext,
      });
      return;
    }

    executeAction(generatedPdf);
  };

  const buildSignedPdf = async (
    context: EmailActionRequestContext,
    managerSignature: string,
  ): Promise<string> => {
    const startDate = new Date(context.startDate);
    const endDate = new Date(context.endDate);
    const pdfDaysSplit = resolvePdfDaysSplit(
      context as unknown as Record<string, unknown>,
    );
    const template: LeaveApplicationPDFTemplateType =
      pdfDaysSplit.previousYearDaysUsedForRequest > 0 &&
      pdfDaysSplit.currentYearDaysUsedForRequest > 0
        ? "mixed-year"
        : "single-year";

    const period =
      formatDate(startDate) === formatDate(endDate)
        ? formatDate(startDate)
        : `${formatDate(startDate)} — ${formatDate(endDate)}`;

    const pdfBlob = await pdf(
      <LeaveApplicationPDFRenderer
        template={template}
        name={context.employeeName || "---"}
        position={context.employeeJobTitle || "---"}
        days={context.duration ?? 0}
        period={period}
        date={new Date()}
        requestYear={context.requestYear ?? startDate.getFullYear()}
        substitute={context.substituteName || "----"}
        employeeSignature={context.employeeSignature || ""}
        managerSignature={managerSignature}
        gender={context.employeeGender ?? undefined}
        requestType={context.requestType ?? undefined}
        unpaidLeaveReason={context.unpaidLeaveReason ?? undefined}
        previousYearDaysUsedForRequest={
          pdfDaysSplit.previousYearDaysUsedForRequest
        }
        currentYearDaysUsedForRequest={
          pdfDaysSplit.currentYearDaysUsedForRequest
        }
      />,
    ).toBlob();

    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(pdfBlob);
    });
  };

  useEffect(() => {
    const managerSignatureImage = response?.requestContext?.managerSignatureImage;
    if (
      response?.result !== "NEEDS_SIGNATURE" ||
      !managerSignatureImage ||
      !response.requestContext ||
      autoSignAttempted.current
    ) {
      return;
    }

    autoSignAttempted.current = true;
    const context = response.requestContext;

    (async () => {
      try {
        const pdfDataUrl = await buildSignedPdf(context, managerSignatureImage);
        await executeAction(pdfDataUrl);
      } catch {
        setResponse({
          result: "INVALID",
          message: "Could not sign the request with your saved signature.",
        });
      }
    })();
  }, [response]);

  const handleSaveSignature = async () => {
    if (!signatureRef.current) {
      return;
    }

    if (signatureRef.current.isEmpty()) {
      setResponse({
        result: "NEEDS_SIGNATURE",
        message: "Please add your signature before approving.",
        requestContext: response?.requestContext,
      });
      return;
    }

    if (!response?.requestContext) {
      setResponse({
        result: "INVALID",
        message: "Missing request context for PDF generation.",
      });
      return;
    }

    const signature = signatureRef.current.toDataURL("image/png");

    setIsGeneratingPdf(true);
    try {
      const context = response.requestContext;
      const pdfDataUrl = await buildSignedPdf(context, signature);

      setGeneratedPdf(pdfDataUrl);
      setResponse({
        result: "NEEDS_SIGNATURE",
        message: "Signature saved. PDF generated. You can now approve.",
        requestContext: context,
      });
    } catch {
      setGeneratedPdf(null);
      setResponse({
        result: "INVALID",
        message: "Could not generate the signed PDF.",
      });
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  const alertType =
    response?.result === "SUCCESS"
      ? "success"
      : response?.result === "NEEDS_SIGNATURE"
        ? "info"
      : response?.result === "INVALID"
        ? "error"
        : "warning";

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card style={{ width: "100%", maxWidth: 520 }}>
        <Typography.Title level={3}>Email Action</Typography.Title>

        {isExecuting && !response ? (
          <div className="flex items-center gap-2">
            <Spin size="small" />
            <Typography.Text>Processing action...</Typography.Text>
          </div>
        ) : response?.result === "NEEDS_SIGNATURE" &&
          response.requestContext?.managerSignatureImage ? (
          <div className="flex items-center gap-2">
            <Spin size="small" />
            <Typography.Text>
              Approving with your saved signature...
            </Typography.Text>
          </div>
        ) : response?.result === "NEEDS_SIGNATURE" ? (
          <div className="flex flex-col gap-3">
            <Alert
              type={alertType}
              showIcon
              message={response?.message ?? "Manager signature is required."}
            />
            <Typography.Text strong>Manager Signature</Typography.Text>
            <div
              style={{
                border: "1px solid #d9d9d9",
                borderRadius: 8,
                width: "100%",
                height: 220,
                overflow: "hidden",
              }}
            >
              <SignatureCanvas
                ref={signatureRef}
                penColor="black"
                canvasProps={{
                  style: { width: "100%", height: "100%" },
                }}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="default"
                onClick={handleSaveSignature}
                loading={isGeneratingPdf}
                disabled={isGeneratingPdf || isExecuting}
              >
                Save Signature
              </Button>
              <Button onClick={handleClearSignature} disabled={isGeneratingPdf}>
                Clear
              </Button>
              <Button
                type="primary"
                onClick={handleApproveWithSignature}
                loading={isExecuting || isGeneratingPdf}
                disabled={!generatedPdf}
              >
                Sign and Approve
              </Button>
            </div>
          </div>
        ) : (
          <Alert
            type={alertType}
            showIcon
            message={response?.message ?? "Unknown result."}
          />
        )}
      </Card>
    </div>
  );
};

export default EmailAction;
